import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client, { apiErr } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/lib/useCurrency";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Zap, Check, Loader2, Sparkles } from "lucide-react";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Billing() {
  const { user, wallet, refreshWallet } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [plans, setPlans] = useState([]);
  const [txns, setTxns] = useState([]);
  const [payments, setPayments] = useState([]);
  const [busy, setBusy] = useState(null);
  const [gateway, setGateway] = useState("razorpay");
  const cur = useCurrency();

  const loadAll = () => {
    client.get("/packages").then(({ data }) => setPackages(data));
    client.get("/plans").then(({ data }) => setPlans(data));
    client.get("/credits/transactions?limit=15").then(({ data }) => setTxns(data.items));
    client.get("/settings/public").then(({ data }) => setGateway(data.payment_gateway || "razorpay")).catch(() => {});
  };
  useEffect(loadAll, []);

  const buy = async (pkg) => {
    setBusy(pkg.id);
    try {
      if (gateway === "stripe") {
        const { data } = await client.post("/payments/stripe/create-session", {
          package_id: pkg.id,
          origin_url: window.location.origin,
        });
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
        toast.error("Could not initiate Stripe checkout");
        return;
      }
      const { data } = await client.post("/payments/create-order", { package_id: pkg.id });
      if (data.mock_mode || !data.provider_order_id) {
        // MOCK payment mode: simulate a successful payment (test only)
        const res = await client.post("/payments/verify", { order_id: data.order_id });
        toast.success(`${res.data.credits_added} credits added!`);
        await refreshWallet(); loadAll();
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) return toast.error("Could not load payment gateway");
      const rzp = new window.Razorpay({
        key: data.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID, amount: data.amount, currency: data.currency,
        name: "AdGen Store", description: `${pkg.name} · ${pkg.credits} credits`,
        order_id: data.provider_order_id,
        prefill: { name: user?.name, email: user?.email },
        theme: { color: "#00CFFF" },
        modal: { ondismiss: () => { setBusy(null); toast("Payment cancelled"); } },
        handler: async (r) => {
          try {
            const res = await client.post("/payments/verify", {
              order_id: data.order_id, razorpay_order_id: r.razorpay_order_id,
              razorpay_payment_id: r.razorpay_payment_id, razorpay_signature: r.razorpay_signature,
            });
            toast.success(`${res.data.credits_added} credits added!`);
            await refreshWallet(); loadAll();
          } catch (e) { toast.error(apiErr(e, "Payment verification failed")); }
          finally { setBusy(null); }
        },
      });
      rzp.on("payment.failed", (resp) => {
        setBusy(null);
        toast.error(resp?.error?.description || "Payment failed. Please try again.");
      });
      rzp.open();
    } catch (e) {
      toast.error(apiErr(e, "Payment failed"));
    } finally {
      setBusy(null);
    }
  };

  const subscribe = async (plan) => {
    setBusy(plan.id);
    try {
      const { data } = await client.post("/subscriptions/create", { plan_id: plan.id });
      // Razorpay Subscriptions returns a hosted "short_url" the customer must
      // visit to authorise the autopay mandate. Redirect them there.
      if (data.short_url) {
        toast.success("Redirecting to Razorpay to authorise autopay…");
        window.location.href = data.short_url;
        return;
      }
      // Fallback for the (rare) legacy free-plan path where no Razorpay
      // subscription is required — grants credits inline.
      toast.success(`Subscribed to ${data.plan || plan.name}!`);
      await refreshWallet(); loadAll();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Credits & Billing</h1>
          <p className="mt-2 text-muted-foreground">Manage your credits and subscription.</p>
        </div>
        <div className="rounded-2xl border border-border bg-card px-6 py-4 text-right">
          <p className="text-xs text-muted-foreground">Current balance</p>
          <p className="flex items-center gap-1.5 text-3xl font-bold"><Zap className="h-6 w-6 text-primary" fill="currentColor" />{wallet.available_balance}</p>
        </div>
      </div>

      <Tabs defaultValue="credits" className="mt-10">
        <TabsList>
          <TabsTrigger value="credits" data-testid="tab-credit-packages">Credit Packs</TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans">Subscription Plans</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="credits">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {packages.map((p) => (
              <div key={p.id} data-testid={`pricing-package-${p.id}`} className={`studio-card relative flex flex-col p-6 ${p.featured ? "ring-2 ring-primary" : ""}`}>
                {p.featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">Best Value</span>}
                <p className="font-bold">{p.name}</p>
                <p className="mt-3 flex items-baseline gap-1"><span className="text-3xl font-extrabold" data-testid={`pkg-price-${p.id}`}>{cur.format(p.price)}</span></p>
                <p className="mt-1 flex items-center gap-1.5 text-sm text-primary font-semibold"><Zap className="h-4 w-4" fill="currentColor" />{p.credits} credits</p>
                <Button className="mt-6 w-full rounded-full" data-testid={`buy-package-${p.id}`} disabled={busy === p.id} onClick={() => buy(p)}>
                  {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buy Now"}
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="plans">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {plans.map((p) => (
              <div key={p.id} className={`studio-card flex flex-col p-6 ${p.name === "Creator" ? "ring-2 ring-primary" : ""}`}>
                <p className="font-bold">{p.name}</p>
                <p className="mt-3 flex items-baseline gap-1"><span className="text-3xl font-extrabold">{cur.format(p.price)}</span><span className="text-sm text-muted-foreground">/mo</span></p>
                <p className="mt-1 flex items-center gap-1.5 text-sm text-primary font-semibold"><Zap className="h-4 w-4" fill="currentColor" />{p.credits} credits/mo</p>
                <ul className="mt-4 flex-1 space-y-2 text-sm text-muted-foreground">
                  {p.features.map((f, i) => <li key={i} className="flex gap-2"><Check className="h-4 w-4 shrink-0 text-primary" />{f}</li>)}
                </ul>
                <Button variant={p.price === 0 ? "outline" : "default"} className="mt-6 w-full rounded-full" data-testid={`subscribe-${p.id}`} disabled={busy === p.id} onClick={() => subscribe(p)}>
                  {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : p.price === 0 ? "Current" : "Subscribe"}
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="studio-card overflow-hidden">
            {txns.length === 0 ? (
              <p className="p-8 text-center text-sm text-muted-foreground">No transactions yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-secondary/40 text-left text-xs uppercase text-muted-foreground">
                  <tr><th className="p-3">Type</th><th className="p-3">Description</th><th className="p-3 text-right">Amount</th><th className="p-3 text-right">Balance</th><th className="p-3 text-right">Date</th></tr>
                </thead>
                <tbody>
                  {txns.map((t) => (
                    <tr key={t.id} className="border-b border-border/60">
                      <td className="p-3"><span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium">{t.type}</span></td>
                      <td className="p-3 text-muted-foreground">{t.description}</td>
                      <td className={`p-3 text-right font-semibold ${t.amount >= 0 ? "text-emerald-600" : "text-destructive"}`}>{t.amount >= 0 ? "+" : ""}{t.amount}</td>
                      <td className="p-3 text-right">{t.balance_after}</td>
                      <td className="p-3 text-right text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
